import { cn } from '@/shadcn/lib/utils';

import { REFERENCED_BY, REFERENCES } from '@/constant/chainscript';
import type { LinkQueryResult } from '../traceInspector.types';
import { Reference } from './reference/reference';

// @todo: place fragment in its own file

type Props = {
  link: LinkQueryResult;
  traceId: string;
  handleClick: () => void;
  collapsed: boolean;
};

export const References = ({
  link,
  traceId,
  handleClick,
  collapsed
}: Props) => {
  const referenceNodes = link.references.nodes.filter(node => node.workflow);
  const referencedByNodes = link.referencedBy.nodes.filter(
    node => node.workflow
  );

  const referencesCount = referenceNodes.length + referencedByNodes.length;

  if (!referencesCount) return null;

  if (referencesCount === 1) {
    return (
      <div
        className="mb-4 flex min-h-9 items-center rounded-md border border-border bg-muted px-3 text-muted-foreground text-sm before:mr-2 before:font-bold before:content-[attr(data-prefix)]"
        data-prefix="*Reference"
      >
        <Reference
          link={
            referencedByNodes.length ? referencedByNodes[0] : referenceNodes[0]
          }
          refCount={referencesCount}
          referenceType={referencedByNodes.length ? REFERENCED_BY : REFERENCES}
          traceId={traceId}
          workflowId={link.workflowId}
        />
      </div>
    );
  }

  return (
    <dl className="mb-4.5 border border-border bg-card">
      <dt
        className={cn(
          'mb-4 flex min-h-9 items-center rounded-md border border-border bg-muted px-3 text-muted-foreground text-sm before:mr-2 before:font-bold before:content-[attr(data-prefix)]',
          !collapsed && 'cursor-pointer'
        )}
        data-prefix="*References:"
        onClick={handleClick}
      >
        This action has {referencesCount} references.
        <span className="flex grow items-center justify-end text-[11px] text-muted-foreground">
          {collapsed ? 'View details' : 'close'}
          <svg
            aria-hidden="true"
            className="ml-[5px] block size-4.5 shrink-0 fill-primary"
            fillRule="evenodd"
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
          >
            {collapsed ? (
              <path
                d="M15.99 23.59c-.501.005-.965-.263-1.21-.7L7.64 10.52c-.239-.429-.239-.951 0-1.38.251-.436.717-.703 1.22-.7h14.29c.5-.003.963.265 1.21.7.25.437.25.973 0 1.41l-7.14 12.34c-.253.439-.724.706-1.23.7zM8.86 9.71c-.028-.016-.062-.016-.09 0-.023.029-.023.071 0 .1l7.14 12.43c.058.02.122.02.18 0l7.15-12.38c.011-.032.011-.068 0-.1-.029-.013-.061-.013-.09 0L8.86 9.71z"
                fillRule="nonzero"
              />
            ) : (
              <path
                d="M15.99 8.44c-.501-.005-.965.263-1.21.7L7.64 21.51c-.239.429-.239.951 0 1.38.251.436.717.703 1.22.7h14.29c.5.003.963-.264 1.21-.7.25-.437.25-.973 0-1.41L17.22 9.14c-.253-.439-.724-.706-1.23-.7zM8.86 22.32c-.028.016-.062.016-.09 0-.023-.029-.023-.071 0-.1l7.14-12.43c.058-.02.122-.02.18 0l7.15 12.38c.011.033.011.068 0 .1-.029.013-.061.013-.09 0l-14.29.05z"
                fillRule="nonzero"
              />
            )}
          </svg>
        </span>
      </dt>
      <dd className={cn(collapsed && 'hidden')}>
        <ul>
          {referenceNodes.map((referenceLink, index) => (
            <li
              className="flex min-h-9 items-center border border-border p-2"
              key={`references_${index}`}
            >
              <Reference
                link={referenceLink}
                refCount={referencesCount}
                referenceType={REFERENCES}
                traceId={traceId}
                workflowId={link.workflowId}
              />
            </li>
          ))}
          {referencedByNodes.map((referenceLink, index) => (
            <li
              className="flex min-h-9 items-center border border-border p-2"
              key={`referenced_by_${index}`}
            >
              <Reference
                link={referenceLink}
                refCount={referencesCount}
                referenceType={REFERENCED_BY}
                traceId={traceId}
                workflowId={link.workflowId}
              />
            </li>
          ))}
        </ul>
      </dd>
    </dl>
  );
};
